import { useCallback, useEffect, useState } from "react";
import { Badge, Box, Button, HStack, Icon, Spinner, Stack, Text, VStack } from "@chakra-ui/react";
import { HiOutlineDevicePhoneMobile, HiOutlineEnvelope, HiOutlineShieldCheck } from "react-icons/hi2";
import AccountService from "@/services/AccountService";
import { SetupTotpDialog, SetupEmailDialog, DisableTwoFactorDialog } from "./TwoFactorDialogs";

const MethodOption = ({ icon, title, description, actionLabel, primary, onAction }) => (
    <Box flex={1} borderWidth="1px" borderColor="border.primary" borderRadius="md" p={4}>
        <VStack align="flex-start" gap={2} h="full">
            <HStack gap={2} color="white">
                <Icon fontSize={18}>{icon}</Icon>
                <Text fontWeight="semibold" fontSize="sm">{title}</Text>
            </HStack>
            <Text color="text.grey" fontSize="xs" flex={1}>{description}</Text>
            <Button size="sm" variant={primary ? "primary" : "outlined"} onClick={onAction}>{actionLabel}</Button>
        </VStack>
    </Box>
);

const AccountPage = () => {
    const [profile, setProfile] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [openDialog, setOpenDialog] = useState(null); // 'totp' | 'email' | 'disable'

    const load = useCallback(() => {
        setLoadError(false);
        AccountService.getProfile()
            .then(setProfile)
            .catch(() => setLoadError(true));
    }, []);

    useEffect(() => { load(); }, [load]);

    const closeDialog = () => setOpenDialog(null);

    return (
        <VStack w="full" alignItems="flex-start" rowGap={6}>
            <Text fontSize="xl" fontWeight="semibold">Account</Text>
            <Box w="full" maxW="720px" bg="bg.card" borderWidth="1px" borderColor="border.primary" borderRadius="md" p={6}>
                <VStack alignItems="stretch" rowGap={4}>
                    <HStack gap={3}>
                        <Icon fontSize={22} color="white"><HiOutlineShieldCheck /></Icon>
                        <Box>
                            <Text fontWeight="medium" color="white">Two-factor authentication (2FA)</Text>
                            <Text color="text.grey" fontSize="sm">Require a second code after your password when signing in to the admin panel.</Text>
                        </Box>
                    </HStack>

                    {loadError ? (
                        <Text color="red.400" fontSize="sm">Couldn't load your account. Refresh the page to try again.</Text>
                    ) : !profile ? (
                        <VStack py={6}><Spinner color="white" /></VStack>
                    ) : profile.twoFactorEnabled ? (
                        <VStack alignItems="flex-start" gap={3}>
                            <HStack gap={2} flexWrap="wrap">
                                <Badge colorPalette="green" variant="solid">2FA enabled</Badge>
                                <Text color="white" fontSize="sm">
                                    {profile.twoFactorMethod === "Email" ? "Method: e-mail code" : "Method: Authenticator app"}
                                </Text>
                                {profile.twoFactorEnabledAt && (
                                    <Text color="text.grey" fontSize="xs">
                                        since {new Date(profile.twoFactorEnabledAt).toLocaleDateString()}
                                    </Text>
                                )}
                            </HStack>
                            <Button size="sm" variant="outlined" onClick={() => setOpenDialog("disable")}>Disable 2FA</Button>
                        </VStack>
                    ) : (
                        <Stack direction={{ base: "column", md: "row" }} gap={3} align="stretch">
                            <MethodOption
                                icon={<HiOutlineDevicePhoneMobile />}
                                title="Authenticator app"
                                description="A code from Google Authenticator or Microsoft Authenticator. The most secure option."
                                actionLabel="Set up app"
                                primary
                                onAction={() => setOpenDialog("totp")}
                            />
                            <MethodOption
                                icon={<HiOutlineEnvelope />}
                                title="E-mail code"
                                description="A one-time code sent to your e-mail address every time you sign in."
                                actionLabel="Enable e-mail code"
                                onAction={() => setOpenDialog("email")}
                            />
                        </Stack>
                    )}
                </VStack>
            </Box>

            <SetupTotpDialog open={openDialog === "totp"} onClose={closeDialog} onEnabled={load} />
            <SetupEmailDialog open={openDialog === "email"} email={profile?.email} onClose={closeDialog} onEnabled={load} />
            <DisableTwoFactorDialog open={openDialog === "disable"} method={profile?.twoFactorMethod} onClose={closeDialog} onDisabled={load} />
        </VStack>
    );
};

export default AccountPage;
